import { observer } from 'mobx-react-lite'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { Typography, Tooltip } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useMyTeamStore } from 'src/feature/my-team/store/my-team.store'
import { PerformanceReviewTemplate } from 'src/feature/my-team/model/my-team-performance'
import { PageMetrics } from 'src/feature/my-team/component/molecules/PageMetrics'
import { ReviewResponseDialog } from './ReviewResponseDialog'
import { Word } from 'src/feature/localization/component/atom/Word'
import { ColorTokens } from 'src/feature/theme/model/Palette'

export type UserPerformanceReviewsProps = {
  userId?: string
}

type ReviewData = {
  template: PerformanceReviewTemplate
  completionPercentage: number
}

const prepareReviewData = (templates: PerformanceReviewTemplate[]): ReviewData[] => {
  return templates
    .filter(template => template.isActive)
    .map(template => ({
      template,
      completionPercentage: template.completionRate ?? 0,
    }))
}

export const UserPerformanceReviews = observer(({ userId }: UserPerformanceReviewsProps) => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  const t = (translation.pages.myTeam as any).performanceReview
  const { isLoading } = myTeamStore.getGenericFieldsState('getPerformanceReviewTemplatesByUser')
  const [reviewData, setReviewData] = useState<ReviewData[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<PerformanceReviewTemplate | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const formatDate = useCallback((date?: string) => {
    return date ? new Date(date).toLocaleDateString() : '-'
  }, [])

  const handleEdit = useCallback((template: PerformanceReviewTemplate) => {
    setSelectedTemplate(template)
    setIsDialogOpen(true)
  }, [])

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false)
    setSelectedTemplate(null)
  }, [])

  useEffect(() => {
    myTeamStore.getPerformanceReviewTemplatesByUser(userId)
  }, [userId, myTeamStore])

  useEffect(() => {
    const userReviewData = prepareReviewData(myTeamStore.performanceReviewTemplates)
    setReviewData(userReviewData)
  }, [myTeamStore.performanceReviewTemplates])

  const getTemplateMetrics = useCallback((data: ReviewData) => {
    const totalQuestions = data.template.questions?.length || 0
    const answeredCount = Math.round((data.completionPercentage / 100) * totalQuestions)

    return [
      { label: t.questions(), value: totalQuestions },
      { label: t.completed(), value: answeredCount },
      { label: t.progress(), value: `${data.completionPercentage.toFixed(0)}%`, valueColor: data.completionPercentage === 100 ? 'success.main' : undefined },
      { label: t.dueDate(), value: formatDate(data.template.dueDate) },
    ]
  }, [formatDate, t])

  const { todoReviews, finishedReviews } = useMemo(() => ({
    todoReviews: reviewData.filter(d => d.completionPercentage < 100),
    finishedReviews: reviewData.filter(d => d.completionPercentage === 100),
  }), [reviewData])

  const renderReviewCard = useCallback((data: ReviewData) => (
    <AppPaper key={data.template.id} sx={styles.card}>
      <AppBox sx={styles.cardHeader}>
        <Tooltip title={data.template.name} arrow>
          <Typography variant='h3' sx={styles.cardTitle} noWrap>
            {data.template.name}
          </Typography>
        </Tooltip>
      </AppBox>

      <PageMetrics
        sx={styles.metrics}
        metrics={getTemplateMetrics(data)}
        isLoading={isLoading}
        backgroundColor='background.dark'
      />

      <AppButton
        textValue={userId ? t.viewAnswers() : t.answer()}
        fontSize={12}
        variant='contained'
        onClick={() => handleEdit(data.template)}
      />
    </AppPaper>
  ), [getTemplateMetrics, isLoading, userId, t, handleEdit])

  return (
    <AppBox flexDirection='column' gap={10}>
      {reviewData.length === 0 ? (
        <AppPaper sx={styles.emptyState}>
          <Typography variant='h5' fontWeight={600}>{t.title()}</Typography>
          <Caption sx={styles.emptyStateText}>{t.noActiveReviews()}</Caption>
        </AppPaper>
      ) : (
        <>
          <Typography variant='h5'>{t.title()}</Typography>

          {/* To-Do Section */}
          <AppBox flexDirection='column' gap={10}>
            <AppBox alignItems='center' gap={5}>
              <Typography fontWeight={500} fontSize={13}>{t.toDo()}</Typography>
              <Word sx={styles.pill}>{todoReviews.length || '0'}</Word>
            </AppBox>
            <AppBox sx={styles.reviewGrid}>
              {todoReviews.map(renderReviewCard)}
            </AppBox>
          </AppBox>

          {/* Finished Section */}
          <AppBox flexDirection='column' gap={10}>
            <AppBox alignItems='center' gap={5}>
              <Typography fontWeight={500} fontSize={13}>{t.finished()}</Typography>
              <Word sx={styles.pill}>{finishedReviews.length || '0'}</Word>
            </AppBox>
            <AppBox sx={styles.reviewGrid}>
              {finishedReviews.map(renderReviewCard)}
            </AppBox>
          </AppBox>
        </>
      )}

      {selectedTemplate && (
        <ReviewResponseDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          template={selectedTemplate}
          userId={userId}
          readOnly={Boolean(userId)}
        />
      )}
    </AppBox>
  )
})

const styles = {
  pill: {
    width: 25,
    height: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    backgroundColor: ColorTokens.primary10,
    color: ColorTokens.primary500,
  },
  emptyState: {
    border: 0,
    borderRadius: '12px',
    width: '30em',
    height: '15em',
    gap: 10,
    padding: '10px 15px',
  },
  emptyStateText: {
    color: 'text.secondary',
    fontSize: 11,
  },
  reviewGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '10px 15px',
    borderRadius: '10px',
    maxWidth: '30em',
    width: '100%',
    border: 0,
  },
  cardHeader: {
    alignItems: 'center',
    width: '100%',
    marginBottom: 10,
  },
  cardTitle: {
    maxWidth: 200,
  },
  metrics: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10,
  },
}
